import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import { ComposableMap, Geographies, Geography, Marker as SimpleMarker } from 'react-simple-maps';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getDadosMapa } from '../../services/api';
import { formatarMoeda } from '../../utils/formatters';
import { serializarFiltros } from '../../utils/compararFiltros';

// Fix para ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const INDICADORES = [
  { value: 'faturamento', label: 'Faturamento' },
  { value: 'lucro', label: 'Lucro' },
  { value: 'custo', label: 'Custo' },
  { value: 'litros', label: 'Litros' },
  { value: 'abastecimentos', label: 'Abastecimentos' },
];

// Coordenadas dos centros dos estados brasileiros
const CENTROS_ESTADOS = {
  'AC': [-9.0238, -70.8120], // Acre
  'AL': [-9.5713, -36.7820], // Alagoas
  'AP': [1.4144, -51.7860], // Amapá
  'AM': [-4.2633, -65.9042], // Amazonas
  'BA': [-12.9714, -38.5014], // Bahia
  'CE': [-3.7172, -38.5433], // Ceará
  'DF': [-15.7942, -47.8822], // Distrito Federal
  'ES': [-19.1834, -40.3089], // Espírito Santo
  'GO': [-16.6864, -49.2643], // Goiás
  'MA': [-2.5387, -44.2825], // Maranhão
  'MT': [-12.6819, -56.9211], // Mato Grosso
  'MS': [-20.7722, -54.7852], // Mato Grosso do Sul
  'MG': [-19.9167, -43.9345], // Minas Gerais
  'PA': [-1.4558, -48.5044], // Pará
  'PB': [-7.2400, -36.7820], // Paraíba
  'PR': [-25.4284, -49.2733], // Paraná
  'PE': [-8.0476, -34.8770], // Pernambuco
  'PI': [-8.8137, -42.5157], // Piauí
  'RJ': [-22.9068, -43.1729], // Rio de Janeiro
  'RN': [-5.7945, -35.2110], // Rio Grande do Norte
  'RS': [-30.0346, -51.2177], // Rio Grande do Sul
  'RO': [-11.5057, -63.5806], // Rondônia
  'RR': [1.4144, -61.4444], // Roraima
  'SC': [-27.2423, -50.2189], // Santa Catarina
  'SP': [-23.5505, -46.6333], // São Paulo
  'SE': [-10.5741, -37.3857], // Sergipe
  'TO': [-10.1753, -48.2982], // Tocantins
};

const MapaPostos = ({ filtros = {}, onCrossFilter, filtroCruzadoAtivo, semWrapper = false }) => {
  const [indicador, setIndicador] = useState('faturamento');
  const [visualizacao, setVisualizacao] = useState('estados'); // 'postos' ou 'estados'
  const [postos, setPostos] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalVendido, setTotalVendido] = useState(0);

  // Handler para clique no posto
  const handlePostoClick = (posto) => {
    if (onCrossFilter) {
      onCrossFilter('posto', posto.posto);
    }
  };

  // Handler para clique no estado
  const handleEstadoClick = (estado) => {
    if (onCrossFilter) {
      onCrossFilter('estado', estado.sigla);
    }
  };

  // Verificar se um posto está selecionado
  // filtroCruzadoAtivo agora é um objeto { posto: {...}, estado: {...}, ... }
  const isPostoSelected = (posto) => {
    return filtroCruzadoAtivo?.posto?.valor === posto.posto;
  };

  // Verificar se um estado está selecionado
  const isEstadoSelected = (estado) => {
    return filtroCruzadoAtivo?.estado?.valor === estado.sigla;
  };

  // Memoizar a serialização dos filtros para evitar chamadas desnecessárias
  const filtrosSerializados = useMemo(() => serializarFiltros(filtros), [filtros]);
  const fetchTimeoutRef = useRef(null);

  useEffect(() => {
    // Limpar timeout anterior se existir
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Delay maior para dar tempo do prefetch completar (batch de 39 requests leva ~10s)
    // Se o prefetch já completou, o cache estará quente e retornará instantaneamente
    fetchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resultado = await getDadosMapa(filtros, indicador);

        // Processar dados do mapa
        const postosProcessados = resultado.map(item => {
          const location = item['visao_geral.location'] || '';
          let latitude = null;
          let longitude = null;

          // Processar location (pode vir como string "lat,lng" ou objeto)
          if (typeof location === 'string' && location.trim() !== '') {
            if (location.includes(',')) {
              const parts = location.split(',').map(v => v.trim());
              if (parts.length >= 2) {
                const lat = parseFloat(parts[0]);
                const lng = parseFloat(parts[1]);
                // Validar se são números válidos e dentro dos ranges corretos
                if (!isNaN(lat) && !isNaN(lng) &&
                  lat >= -90 && lat <= 90 &&
                  lng >= -180 && lng <= 180) {
                  latitude = lat;
                  longitude = lng;
                }
              }
            }
          } else if (typeof location === 'object' && location !== null) {
            const lat = parseFloat(location.latitude || location.lat || location[0]);
            const lng = parseFloat(location.longitude || location.lng || location.lon || location[1]);
            // Validar se são números válidos e dentro dos ranges corretos
            if (!isNaN(lat) && !isNaN(lng) &&
              lat >= -90 && lat <= 90 &&
              lng >= -180 && lng <= 180) {
              latitude = lat;
              longitude = lng;
            }
          }

          return {
            posto: item['visao_geral.posto'] || '',
            estado: item['visao_geral.estado'] || '',
            valor: item['visao_geral.indicador_selecionado'] || 0,
            latitude,
            longitude,
          };
        }).filter(p => {
          // Filtrar apenas postos com coordenadas válidas (não null, não NaN, dentro dos ranges)
          return p.latitude !== null && p.longitude !== null &&
            !isNaN(p.latitude) && !isNaN(p.longitude) &&
            p.latitude >= -90 && p.latitude <= 90 &&
            p.longitude >= -180 && p.longitude <= 180;
        });

        setPostos(postosProcessados);

        // Agrupar por estado
        const estadosAgrupados = postosProcessados.reduce((acc, posto) => {
          const siglaEstado = posto.estado?.trim().toUpperCase() || '';
          if (!siglaEstado || !CENTROS_ESTADOS[siglaEstado]) return acc;

          const [lat, lng] = CENTROS_ESTADOS[siglaEstado];

          if (!acc[siglaEstado]) {
            acc[siglaEstado] = {
              estado: siglaEstado,
              valor: 0,
              quantidade: 0,
              latitude: lat,
              longitude: lng,
            };
          }

          acc[siglaEstado].valor += parseFloat(posto.valor) || 0;
          acc[siglaEstado].quantidade += 1;

          return acc;
        }, {});

        // Converter para array e calcular média
        const estadosArray = Object.values(estadosAgrupados).map(estado => ({
          ...estado,
          valorMedio: estado.quantidade > 0 ? estado.valor / estado.quantidade : 0,
        }));

        setEstados(estadosArray);

        // Calcular total vendido
        const total = postosProcessados.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
        setTotalVendido(total);
      } catch (error) {
        console.error('Erro ao buscar dados do mapa:', error);
        setPostos([]);
        setTotalVendido(0);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms delay para dar tempo do prefetch completar

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [indicador, filtrosSerializados]);

  // Calcular centro do mapa baseado nos postos ou estados
  const calcularCentro = () => {
    if (visualizacao === 'estados') {
      if (estados.length === 0) return [-10, -55]; // Centro do Brasil
      const latMedia = estados.reduce((acc, e) => acc + e.latitude, 0) / estados.length;
      const lngMedia = estados.reduce((acc, e) => acc + e.longitude, 0) / estados.length;
      if (isNaN(latMedia) || isNaN(lngMedia)) return [-10, -55];
      return [latMedia, lngMedia];
    }

    const postosValidos = postos.filter(p =>
      p.latitude !== null && p.longitude !== null &&
      !isNaN(p.latitude) && !isNaN(p.longitude) &&
      p.latitude >= -90 && p.latitude <= 90 &&
      p.longitude >= -180 && p.longitude <= 180
    );

    if (postosValidos.length === 0) return [-10, -55]; // Centro do Brasil

    const latMedia = postosValidos.reduce((acc, p) => acc + p.latitude, 0) / postosValidos.length;
    const lngMedia = postosValidos.reduce((acc, p) => acc + p.longitude, 0) / postosValidos.length;

    // Garantir que não retorne NaN
    if (isNaN(latMedia) || isNaN(lngMedia)) {
      return [-10, -55]; // Centro do Brasil como fallback
    }

    return [latMedia, lngMedia];
  };

  // Função para formatar valores
  const formatarValor = (valor) => {
    const num = typeof valor === 'string' ? parseFloat(valor) : valor;

    if (indicador === 'litros') {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num) + ' L';
    } else if (indicador === 'abastecimentos') {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num);
    }
    return formatarMoeda(num);
  };

  // Função para formatar valores abreviados (R$ 45M)
  const formatarValorAbreviado = (valor) => {
    const num = typeof valor === 'string' ? parseFloat(valor) : valor;

    if (indicador === 'litros') {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M L';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K L';
      }
      return num.toFixed(0) + ' L';
    } else if (indicador === 'abastecimentos') {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toFixed(0);
    }
    // Para valores monetários
    if (num >= 1000000) {
      return 'R$ ' + (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return 'R$ ' + (num / 1000).toFixed(1) + 'K';
    }
    return formatarMoeda(num);
  };

  const conteudo = (
    <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" style={{ gap: 'var(--space-fluid-xs)', marginBottom: 'var(--space-fluid-xs)' }}>
        <h3 className="font-bold text-gray-800" style={{ fontSize: 'var(--text-fluid-sm)' }}>
          {visualizacao === 'estados' ? 'Média/Estado' : 'Mapa Postos'}
        </h3>

        <div className="flex" style={{ gap: 'var(--space-fluid-xs)' }}>
          {/* Botão Toggle Postos/Estados */}
          <button
            onClick={() => setVisualizacao(visualizacao === 'postos' ? 'estados' : 'postos')}
            className="flex items-center bg-white border border-orange-300 rounded-xl font-medium text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ gap: 'var(--space-fluid-xs)', padding: 'var(--space-fluid-xs) var(--space-fluid-sm)', fontSize: 'var(--text-fluid-xs)' }}
          >
            <svg style={{ width: 'var(--icon-fluid-sm)', height: 'var(--icon-fluid-sm)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            {visualizacao === 'postos' ? 'Estados' : 'Postos'}
          </button>

          {/* Seletor de Indicador */}
          <div className="relative">
            <select
              value={indicador}
              onChange={(e) => setIndicador(e.target.value)}
              className="fluid-select"
            >
              {INDICADORES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <svg
              className="absolute top-1/2 -translate-y-1/2 text-amber-500 pointer-events-none"
              style={{ right: 'var(--space-fluid-xs)', width: 'var(--icon-fluid-sm)', height: 'var(--icon-fluid-sm)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Mapa */}
      <div className="flex-1 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
        {loading ? (
          <div className="fluid-spinner-container">
            <div className="flex flex-col items-center" style={{ gap: 'var(--space-fluid-sm)' }}>
              <div className="fluid-spinner"></div>
              <span className="fluid-spinner-text">Carregando mapa...</span>
            </div>
          </div>
        ) : visualizacao === 'estados' && estados.length > 0 ? (
          // Mapa coroplético de estados usando react-simple-maps
          <div className="w-full h-full relative">
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{
                center: [-55, -10], // Melhor centralização do Brasil
                scale: 1100, // Aumentado para melhor enquadramento
              }}
              style={{ width: '100%', height: '150%' }}
              className="focus:outline-none"
            >
              <Geographies geography="https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson">
                {({ geographies }) =>
                  geographies.map((geo) => {
                    // Tentar diferentes campos para identificar o estado
                    const siglaEstado = geo.properties.sigla ||
                      geo.properties.SIGLA ||
                      geo.properties.uf ||
                      geo.properties.UF ||
                      geo.properties.name?.substring(0, 2).toUpperCase() ||
                      '';
                    const estadoData = estados.find(e => e.estado === siglaEstado);
                    const temDados = !!estadoData;
                    const isSelected = filtroCruzadoAtivo?.estado?.valor === siglaEstado;

                    // Cor diferente para estado selecionado
                    const corEstado = isSelected ? '#f59e0b' : (temDados ? '#3b5998' : '#e5e7eb');

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={corEstado}
                        stroke={isSelected ? '#d97706' : '#ffffff'}
                        strokeWidth={isSelected ? 2 : 0.5}
                        onClick={() => temDados && handleEstadoClick({ sigla: siglaEstado })}
                        style={{
                          default: { outline: 'none' },
                          hover: {
                            fill: isSelected ? '#d97706' : (temDados ? '#2d4373' : '#d1d5db'),
                            outline: 'none',
                            cursor: temDados ? 'pointer' : 'default',
                          },
                          pressed: { outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {/* Labels com valores nos estados */}
              {estados.map((estado, index) => {
                const isSelected = filtroCruzadoAtivo?.estado?.valor === estado.estado;
                return (
                  <SimpleMarker
                    key={index}
                    coordinates={[estado.longitude, estado.latitude]}
                  >
                    <g>
                      {/* Retângulo com valor */}
                      <rect
                        x={-115}
                        y={-48}
                        width={120}
                        height={40}
                        fill={isSelected ? 'rgba(245, 158, 11, 0.95)' : 'rgba(255, 255, 255, 0.95)'}
                        stroke={isSelected ? '#d97706' : '#d1d5db'}
                        strokeWidth={isSelected ? 2 : 1}
                        rx={8}
                        style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}
                      />
                      {/* Sigla do estado */}
                      <text
                        textAnchor="middle"
                        y={-33}
                        x={-60}
                        fontSize={14}
                        fill={isSelected ? '#ffffff' : '#1f2937'}
                        fontWeight="bold"
                      >
                        {estado.estado}
                      </text>
                      {/* Valor formatado */}
                      <text
                        textAnchor="middle"
                        y={-17}
                        x={-56}
                        fontSize={16}
                        fill={isSelected ? '#fef3c7' : '#6b7280'}
                        fontWeight={isSelected ? 'bold' : 'bold'}
                      >
                        {formatarValor(estado.valorMedio)}
                      </text>
                    </g>
                  </SimpleMarker>
                );
              })}
            </ComposableMap>
          </div>
        ) : postos.length > 0 ? (
          <MapContainer
            center={calcularCentro()}
            zoom={4}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {(
              // Renderizar postos
              postos
                .filter(posto => {
                  // Validação extra antes de renderizar
                  return posto.latitude !== null && posto.longitude !== null &&
                    !isNaN(posto.latitude) && !isNaN(posto.longitude) &&
                    posto.latitude >= -90 && posto.latitude <= 90 &&
                    posto.longitude >= -180 && posto.longitude <= 180;
                })
                .map((posto, index) => {
                  const isSelected = isPostoSelected(posto);

                  return (
                    <Marker
                      key={`${posto.posto}-${index}`}
                      position={[posto.latitude, posto.longitude]}
                      eventHandlers={{
                        click: () => handlePostoClick(posto),
                      }}
                    >
                      <Tooltip permanent={false} direction="top" offset={[0, -10]}>
                        <div className="text-center h-6">
                          <p className="font-bold text-[10px] text-gray-800">
                            {posto.posto}
                          </p>
                          <p className="text-xs font-semibold text-orange-600">
                            {formatarValor(posto.valor)}
                          </p>
                        </div>
                      </Tooltip>
                      <Popup>
                        <div className="p-2">
                          <p className={`font-bold text-sm mb-1 ${isSelected ? 'text-amber-600' : ''}`}>
                            {isSelected && '✓ '}{posto.posto}
                          </p>
                          <p className="text-xs text-gray-600">
                            {formatarValor(posto.valor)}
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            Clique para filtrar
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })
            )}
          </MapContainer>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            Nenhum dado disponível
          </div>
        )}
      </div>

      {/* Footer com total */}
      {!loading && (visualizacao === 'estados' ? estados.length > 0 : postos.length > 0) && (
        <div className="flex justify-end mt-2 mb-2">
          <div className="fluid-stat-badge">
            <span className="fluid-stat-label">Total: </span>
            <span className="fluid-stat-value">
              {formatarValorAbreviado(totalVendido)}
            </span>
          </div>
        </div>
      )}
    </>
  );

  if (semWrapper) {
    return <div className="h-full flex flex-col">{conteudo}</div>;
  }

  return (
    <div className="dashboard-card max-h-full">
      {conteudo}
    </div>
  );
};

export default MapaPostos;

